import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaQuestionCircle, FaCog, FaCreditCard, FaShieldAlt, FaStar, FaRobot } from 'react-icons/fa';
import './FAQ.css';

const FAQ = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Questions', icon: FaStar },
    { id: 'ai', name: 'AI-Watchman', icon: FaRobot },
    { id: 'general', name: 'General', icon: FaQuestionCircle },
    { id: 'features', name: 'Features', icon: FaCog },
    { id: 'payments', name: 'Payments', icon: FaCreditCard },
    { id: 'privacy', name: 'Privacy & Security', icon: FaShieldAlt }
  ];

  const faqs = [
    {
      category: 'ai',
      question: t('faq.ai.q1'),
      answer: t('faq.ai.a1')
    },
    {
      category: 'ai',
      question: t('faq.ai.q2'),
      answer: t('faq.ai.a2')
    },
    {
      category: 'ai',
      question: t('faq.ai.q3'),
      answer: t('faq.ai.a3')
    },
    {
      category: 'ai',
      question: t('faq.ai.q4'),
      answer: t('faq.ai.a4')
    },
    {
      category: 'ai',
      question: t('faq.ai.q5'),
      answer: t('faq.ai.a5')
    },
    {
      category: 'ai',
      question: t('faq.ai.q6'),
      answer: t('faq.ai.a6')
    },
    {
      category: 'ai',
      question: t('faq.ai.q7'),
      answer: t('faq.ai.a7')
    },
    {
      category: 'ai',
      question: t('faq.ai.q8'),
      answer: t('faq.ai.a8')
    },
    {
      category: 'general',
      question: 'What is Land Tracker?',
      answer: 'Land Tracker is a comprehensive mobile app designed to help property owners manage their land assets. It provides tools for property documentation, GPS boundary capture, tax reminders, family tree preservation, and secure document storage - all in one convenient platform.'
    },
    {
      category: 'general',
      question: 'Do I need internet to use the app?',
      answer: 'Most features work offline, including viewing your properties, photos, and saved documents. However, you need internet for initial sync, uploading new documents, accessing maps, and cloud backup.'
    },
    {
      category: 'general',
      question: 'Which devices are supported?',
      answer: 'Land Tracker is available for iOS (iPhone and iPad) and Android devices. We support iOS 12.0+ and Android 6.0+ for the best experience.'
    },
    {
      category: 'general',
      question: 'Can I use it on multiple devices?',
      answer: 'Yes! Your account syncs across all your devices. Sign in with the same account on your phone, tablet, or any other device to access your data everywhere.'
    },
    {
      category: 'features',
      question: 'How does GPS boundary capture work?',
      answer: 'Walk around the perimeter of your property with your phone. The app uses GPS to record coordinates and automatically calculates the area. You can save, edit, and share these boundaries anytime.'
    },
    {
      category: 'features',
      question: 'What file types can I upload as documents?',
      answer: 'You can upload PDFs, images (JPG, PNG), Word documents, Excel spreadsheets, and most common file formats. Each file can be up to 50MB in the free plan and unlimited in Premium/Business plans.'
    },
    {
      category: 'features',
      question: 'How do tax reminders work?',
      answer: 'Set custom reminder dates for property taxes, and we\'ll send you push notifications before deadlines. You can also track payment history and attach receipts for your records.'
    },
    {
      category: 'features',
      question: 'Can I add multiple properties?',
      answer: 'Free plan allows up to 3 properties. Premium and Business plans support unlimited properties with no restrictions.'
    },
    {
      category: 'payments',
      question: 'How do I upgrade to Premium?',
      answer: 'Tap on "Upgrade" in the app or visit our pricing page. Choose your billing cycle (monthly or yearly) and complete the payment. Your upgrade is instant!'
    },
    {
      category: 'payments',
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, MasterCard, American Express), debit cards, Apple Pay, Google Pay, and other digital payment methods through our secure payment processor.'
    },
    {
      category: 'payments',
      question: 'Can I cancel my subscription anytime?',
      answer: 'Absolutely! You can cancel anytime from your account settings. Your premium features will remain active until the end of your billing period. No questions asked, no cancellation fees.'
    },
    {
      category: 'payments',
      question: 'Do you offer refunds?',
      answer: 'Yes, we offer a 14-day money-back guarantee for all paid plans. If you\'re not satisfied for any reason, contact us within 14 days of purchase for a full refund.'
    },
    {
      category: 'privacy',
      question: 'Is my data secure?',
      answer: 'Yes! We use bank-level AES-256 encryption for data storage and TLS/SSL for data transmission. Your documents and property information are stored securely in the cloud with automatic backups.'
    },
    {
      category: 'privacy',
      question: 'Who can see my property information?',
      answer: 'Only you can access your data. We never share, sell, or rent your personal information to third parties. In Business plans, you can choose to share specific properties with team members.'
    },
    {
      category: 'privacy',
      question: 'How do I delete my account?',
      answer: 'Go to Settings > Account > Delete Account. This will permanently remove all your data from our servers. You can also contact support if you need assistance.'
    },
    {
      category: 'privacy',
      question: 'Do you use my location data?',
      answer: 'We only use location data when you actively use GPS features like boundary capture or adding property locations. You can disable location permissions anytime in your device settings.'
    }
  ];

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const filteredFAQs = activeCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);

  return (
    <div className="faq-page">
      {/* Hero Section */}
      <section className="faq-hero" data-aos="fade-up">
        <div className="container">
          <h1>Frequently Asked Questions</h1>
          <p className="faq-subtitle">Find answers to common questions about Land Tracker</p>
        </div>
      </section>

      {/* Categories */}
      <section className="faq-content">
        <div className="container">
          <div className="categories" data-aos="fade-up">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                  onClick={() => setActiveCategory(category.id)}
                >
                  <Icon />
                  <span>{category.name}</span>
                </button>
              );
            })}
          </div>

          {/* FAQ Accordion */}
          <div className="faq-accordion" data-aos="fade-up">
            {filteredFAQs.map((faq, index) => (
              <div 
                key={index} 
                className={`faq-item ${openIndex === index ? 'open' : ''}`}
              >
                <button 
                  className="faq-question"
                  onClick={() => toggleAccordion(index)}
                >
                  <span>{faq.question}</span>
                  <FaChevronDown className="chevron" />
                </button>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Still Have Questions */}
          <div className="help-section" data-aos="fade-up">
            <h3>Still have questions?</h3>
            <p>Can't find the answer you're looking for? Our support team is here to help.</p>
            <button className="btn btn-primary">
              Contact Support
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
